import { Injectable } from '@angular/core';
import {
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';

import * as moment from 'moment';
import {
  AddCustomElement,
  FromTo,
  FromToDetail,
  ListFormat,
  ReportCustomElements,
  ReportGlobalImage,
  ReportTemplate,
  ReportTemplateUpdate,
  ReportTitleFormat,
  ResponseIdList,
  TemplateCommonSection,
  TemplateCustomPageSection,
  TemplateDefinition,
  TemplatePageSection,
  TemplateText,
  UpdateIdList,
  UpdateResultList,
} from '../_objects/report-template';

import { ElementType, Location } from '../_objects/element-type';
import { NavigatorElements } from '../_objects/interfaces/navigator-elements';
import { ReportDataPlate } from '../_objects/report-data-plate';
import { ReportDropDownList } from '../_objects/report-dropdown-list';
import {
  ReportElement,
  ValidationErrorDetails,
} from '../_objects/report-element';
import { ReportImage } from '../_objects/report-image';
import { ReportISO } from '../_objects/report-iso';
import { ReportLine } from '../_objects/report-line';
import { ReportMeasurementPoint } from '../_objects/report-measurement-point';
import { ReportModel } from '../_objects/report-model';
import { ReportPage } from '../_objects/report-page';
import { ReportProperties } from '../_objects/report-properties';
import { ReportRectangle } from '../_objects/report-rectangle';
import { ReportSection } from '../_objects/report-section';
import { ReportSignature } from '../_objects/report-signature';
import { ReportSummary } from '../_objects/report-summary';
import { ReportTable } from '../_objects/report-table';
import { ReportText } from '../_objects/report-text';
import { ReportVisualEvents } from '../_objects/report-visual-events';
import {
  DYNAMIC_SELECT_ITEM_VISOL,
  DYNAMIC_SELECT_ITEM_DATAPLATE_VISOL,
  DYNAMIC_SELECT_ITEM_TEXT_VISOL,
  DYNAMIC_SELECT_ITEM_SUMMARYTABLE_VISOL,
  DYNAMIC_SELECT_ITEM_SUBTOTALTABLE_VISOL,
  DYNAMIC_SELECT_ITEM_PICTURETABLE_VISOL,
  ERROR_MESSAGE,
  REGULAR_EXPRESSION,
} from '../app.constants';
import { JSONSyntaxValidator } from '../helpers/json-syntax-validator';
import { ReportCustomElement } from '../_objects/report-custom-element';
import { ReportTemplateService } from './report-template.service';
import { ReportComponentService } from './report-component.service';
import { ReportSubTotal } from '../_objects/report-subtotal';
import { LangUpdateService } from './lang-update.service';
import { TranslateService } from '@ngx-translate/core';

export interface Notifier {
  componentName: string;
  ids: NotifierIds;
}

export interface NotifierIds {
  pageId: string;
  elementId: string;
}

export interface PageScreen {
  pageId: string;
  imageSrc: string;
}

export type FieldType =
  | 'ReadOnly'
  | 'Text'
  | 'TextArea'
  | 'Number'
  | 'Integer'
  | 'DateTime'
  | 'CheckBox'
  | 'Select'
  | 'JSON'
  | 'Multi'
  | 'List'
  | 'Color'
  | 'TableHeader'
  | 'PriorityItems'
  | 'TableColumn'
  | 'DropDownOptionItems'
  | 'ImageInfo';
export type TypeOfJSON = string | number | boolean | object;

export interface PropertyPreviewMetadata {
  name: string;
  parentName: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  isRequired: boolean;
  isEdited: boolean;
  isDisplay: boolean;
  selectItems: { label: string; value?: string | number }[];
  category?:
    | 'GENERAL'
    | 'TABLE'
    | 'COLUMN'
    | 'ROW'
    | 'TITLE'
    | 'IMAGEINFO'
    | 'IMAGEINFOTITLE'
    | 'IMAGEINFOIMAGE';
  group?: 'Position' | 'Size' | 'TextBox' | 'Line' | 'Background' | 'Font' | 'Radio' | 'Select' | 'DataBinding' | 'Input'
  | 'TitlePosition' | 'TitleSize' | 'TitleTextBox' | 'TitleLine' | 'TitleBackground' | 'TitleFont'
  | 'Image'
  ;
  isFieldType(type: FieldType): boolean;
}

export interface ReportPreviewMetadata {
  topPx: number;
  leftPx: number;
  widthPx: number;
  heightPx: number;
}

export interface UpdateMetadata {
  id: string;
  name: string;
  parentName?: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  oldValue: TypeOfJSON | Array<TypeOfJSON>;
}

export interface MousePoint {
  onElementX: number;
  onElementY: number;
}

@Injectable({ providedIn: 'root' })
export class ReportTemplateDesignService {
  public static PROP_DYNAMIC_ATTR4VISOL: string[] = [
    'text',
    'type',
    'dynamicId',
    'editableId',
    'inputType',
    'validation',
    'format',
    'required',
    'maxLength',
    'radioType',
    'radioLabel',
    'options',
    'label',
    'value',
    'inputGroup',
    'src',
    'multiSelectDelimiter'
  ];
  public static PROP_DYNAMIC_ATTR4IPM: string[] = [
    'text',
    'type',
    'dynamicId',
    'editableId',
    'inputType',
    'validation',
    'format',
    'required',
    'maxLength',
    'radioType',
    'radioLabel',
    'options',
    'label',
    'value',
    'inputGroup',
    'src',
    'stageId',
    'itemDataSource',
    'captionsList',
    'titleText',
    'isStriped',
    'tableRules',
    'columns',
    'reservedFor',
    'isoType',
    'dynamicDataField'
  ];

  public  visualEventPropertyList = [
    { label: 'eventId', value: 'Event Id' },
    { label: 'label', value: 'Input'},
    { label: 'location', value: 'Location'},
    { label: 'visualEventType', value: 'VisualEvent Type'},
    { label: 'description', value: 'Description'},
    { label: 'imageUrls', value: 'ImageUrls'},
    { label: 'isPhotoOnlyEvent', value: 'IsPhotoOnly Event'},
    { label: 'visualEventStatus', value: 'VisualEvent Status'},
    { label: 'visualEventPriority', value: 'VisualEvent Priority'},
    { label: 'visualEventRecommendation', value: 'VisualEvent Recommendation'}];
    public  notesPropertyList = [
      { label: 'note', value: 'notes' },
    ];
  public  signaturePropertyList = [
    { label: 'name', value: 'Name'},
    { label: 'signatureBase64', value: 'Signature Image'},
    { label: 'date', value: 'Date'},
    { label: 'role', value: 'Role'}];
  public selectProperties: any[] ;

  public originTemplate: ReportTemplate;
  public customDrawTemplate: ReportTemplate;

  public templateForUndo: ReportTemplate[];
  public templateForRedo: ReportTemplate[];
  public highlightElement: boolean;
  public checkedRuler = false;
  public selectLabel = '';
  public customTemplateDefinition: TemplateCustomPageSection;
  public customTemplateDefinitionOnSave: TemplateCustomPageSection;
  public reportTemplateId: string;
  public customElementPageKey: string;
  public isRedirectingToCustomElement: boolean;
  public reportCustomTemplates: ReportCustomElements[];
  public clickedPageIdForDelete: string;
  public saveSpinnerId = 'saveSpinner';
  public customElementAdd = false;
  public currentCEName: string;
  public currentCEApplication: string;
  public isClickedOnCEUpdate = false;
  public customElementResponse: any;
  public customTemDefinitionBeforeEdit: TemplateDefinition;
  public templateBeforeEditOfCE: ReportTemplate;

  public onReady: Observable<string>;
  public onSelectedElement: Observable<string>;
  public onEndDraw: Observable<string>;
  public onEndUpdate: Observable<string>;
  public onUpdatePageSnap: Observable<string>;

  public drawElement = new BehaviorSubject(null);
  public currentdrawElement = this.drawElement.asObservable();

  public deleteCEItem = new BehaviorSubject(null);
  public currentDeleteCEItem = this.deleteCEItem.asObservable();

  public zoomPage = new BehaviorSubject(null);
  public currentZoomPage = this.zoomPage.asObservable();

  public navigatorElements: NavigatorElements[] = [];
  public drawElementType: ElementType;
  public drawDesignCustomElementId: string;
  public drawDesignCustomElementName: string;
  public pageScreens: PageScreen[] = [];

  private templateId: string;
  private isVisol = false;

  private tempTemplate: ReportTemplate;
  private reportModel: ReportModel;
  private templateImages: ReportGlobalImage[];
  private dynamicDataFields: any;
  private viewPageId: string;
  private stageNames: { label: string; value?: string }[];

  private onReady$: Subject<string>;
  private onSelectedElement$: Subject<string>;
  private onEndDraw$: Subject<string>;
  private onUpdatePageSnap$: Subject<string>;
  private onEndUpdate$: Subject<string>;

  private readonly virtualGridPx: number;

  private dragElements: {
    [id: string]: {
      type: ElementType;
      from: FromToDetail;
      dragPoint: MousePoint;
    };
  };
  private resizeElements: {
    [id: string]: {
      type: ElementType;
      markers: {
        horizontal: string;
        vertical: string;
      };
      beginPoint: MousePoint;
    };
  };

  public constructor(
    private router: Router,
    private reportTemplateService: ReportTemplateService,
    private reportComponentService: ReportComponentService,
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.langService.prepare(this, this.translate);
    this.onReady$ = new Subject();
    this.onReady = this.onReady$.asObservable();

    this.onSelectedElement$ = new Subject();
    this.onSelectedElement = this.onSelectedElement$.asObservable();

    this.onEndDraw$ = new Subject();
    this.onEndDraw = this.onEndDraw$.asObservable();

    this.onEndUpdate$ = new Subject();
    this.onEndUpdate = this.onEndUpdate$.asObservable();

    this.onUpdatePageSnap$ = new Subject();
    this.onUpdatePageSnap = this.onUpdatePageSnap$.asObservable();

    this.virtualGridPx = 15; // 仮値。
    this.templateForUndo = [];
    this.templateForRedo = [];
    this.highlightElement = false;
  }
  public get reportProperties(): ReportElement[] {
    if (!this.reportModel) {
      return null;
    }

    const result = Object.entries(this.reportModel.ElementFinder);
    return result.map((_) => _[1]);
  }

  public initialize(reportTemplateId: string, isVisol: boolean) {
    this.templateId = reportTemplateId;
    this.isVisol = isVisol;
    this.reportTemplateService
      .getReportTemplate(this.templateId, this.isVisol)
      .subscribe((reportTemplate: ReportTemplate) => {
        reportTemplate.template = reportTemplate.reportTemplateContent
          ? JSON.parse(reportTemplate.reportTemplateContent)
          : new TemplateDefinition();
        this.loadTemplate(reportTemplate);
      });
  }
  public loadTemplate(template: ReportTemplate, isCustomElement?: boolean) {
    this.loadReportTemplate(template);
    if (!isCustomElement) {
      this.loadReportTemplateImages();
      this.loadDynamicDataFields(template.application.toUpperCase());
      this.loadStageNames();
      this.loadCustomElements(this.getTemplateApplication());
    }
    this.templateForUndo = [];
    this.templateForRedo = [];
    this.pageScreens = [];
  }

  public loadReportTemplate(template: ReportTemplate, isUndo?: boolean) {
    this.navigatorElements = [];
    this.tempTemplate = template;
    this.originTemplate = JSON.parse(JSON.stringify(template, undefined, 4));
    this.reportModel = new ReportModel(template.template, this.translate, this.langService);
    this.viewPageId = null;
    this.onEndUpdate$.next('Updated');
    if (isUndo) {
      this.ready();
    }
  }

  public reloadReportTemplate(template: ReportTemplate, isUndo?: boolean) {
    this.navigatorElements = [];
    this.tempTemplate = template;
    this.reportModel = new ReportModel(template.template, this.translate, this.langService);
    this.onEndUpdate$.next('Updated');
    if (isUndo) {
      this.ready();
    }
  }

  public loadStageNames(): void {
    this.stageNames = [];
    this.reportTemplateService.getStageNames().subscribe((res) => {
      res.forEach((stage) => {
        this.stageNames.push({ label: stage.name, value: stage.code });
      });
    });
  }

  public loadDynamicDataFields(app: string): void {
    switch (app) {
      case 'IPM':
        this.reportTemplateService.getDynamicBindingsForIpm().subscribe(
          (bindings) => {
            this.dynamicDataFields = bindings;
          },
          (err) => {
            console.log(err);
          }
        );
        break;

      case 'VISOL':
        this.dynamicDataFields = DYNAMIC_SELECT_ITEM_VISOL;
        break;

      default:
        this.dynamicDataFields = [];
        break;
    }
  }

  public loadCustomElements(application: string) {
    this.reportComponentService.getCustomTemplates(application).subscribe((res: ReportCustomElements[]) => {
      this.reportCustomTemplates = res;
    });
  }

  public reloadTemplate(template: ReportTemplate, isCustomElement?: boolean) {
    this.loadTemplate(template, isCustomElement);
  }

  public getOriginTemplate(): ReportTemplate {
    return this.originTemplate;
  }
  public getTempTemplate(): ReportTemplate {
    return this.tempTemplate;
  }
  public getTemplateId(): string {
    return this.tempTemplate.id;
  }
  public getTemplateName(): string {
    return this.tempTemplate.name;
  }
  public getTemplateLastSaved(): string {
    return moment.utc(this.tempTemplate.lastUpdatedDateUtc).local().format();
  }
  public getTemplateList(): ListFormat[] {
    return this.tempTemplate.template.list;
  }
  public getTemplateApplication(): string {
    return this.tempTemplate.application;
  }
  public getTemplateIsValid(): boolean {
    return this.tempTemplate.isValid;
  }
  public getTemplateReportTitleFormat(): ReportTitleFormat[] {
    return this.tempTemplate.reportTitleFormat;
  }
  public getTemplateReportTitleInputMode(): string {
    return this.tempTemplate.reportTitleInputMode;
  }
  public getTemplateEtag(): string {
    return this.tempTemplate.etag;
  }
  public getTemplateSubjectModelIds(): string[] {
    return this.tempTemplate.subjectModelIds;
  }
  public getTemplateReportType(): string {
    return this.tempTemplate.reportType;
  }
  public getTemplateEquipmentType(): string[] | string {
    return this.tempTemplate.equipmentType;
  }
  public getTemplateFontFamily(): string {
    return this.tempTemplate.template.fontFamily;
  }
  public setTemplateName(name: string) {
    this.tempTemplate.name = name;
  }
  public setTemplateApplication(application: string) {
    this.tempTemplate.application = application;
    const reportTemplate = JSON.parse(
      JSON.stringify(this.tempTemplate, undefined, 4)
    );
    reportTemplate.template = this.getTemplate();
    this.loadTemplate(reportTemplate);
  }
  public setTemplateIsValid(isValid: boolean) {
    this.tempTemplate.isValid = isValid;
  }
  public setTemplateEtag(etag: string) {
    this.tempTemplate.etag = etag;
  }
  public setTemplateReportFont(type: string, value: any) {
    if (type === 'fontSize')
      this.tempTemplate.template.fontSize = value;
    else if (type === 'fontFamily')
      this.tempTemplate.template.fontFamily = value;
  }
  public setTemplatePaperSize(paperSize: string) {
    this.tempTemplate.template.paperSize = paperSize;
  }
  public setTemplatePageMargin(type: string, value: number) {
    switch (type) {
      case 'pageMarginTop':
        this.tempTemplate.template.pageMarginTop = value;
        break;
      case 'pageMarginBottom':
        this.tempTemplate.template.pageMarginBottom = value;
        break;
      case 'pageMarginLeft':
        this.tempTemplate.template.pageMarginLeft = value;
        break;
      case 'pageMarginRight':
        this.tempTemplate.template.pageMarginRight = value;
        break;
      default:
        break;
    }
  }
  public setTemplateHeaderHeight(value: number) {
    this.tempTemplate.template.header.height = value;
  }
  public setTemplateFooterHeight(value: number) {
    this.tempTemplate.template.footer.height = value;
  }
  public setTemplateReportTitleFormat(reportTitleFormat: ReportTitleFormat[]) {
    this.tempTemplate.reportTitleFormat = reportTitleFormat;
  }
  public setTemplateReportList(reportObservation: ListFormat[]) {
    this.tempTemplate.template.list = reportObservation;
  }
  public setTemplateReportTitleInputMode(reportTitleInputMode: string) {
    this.tempTemplate.reportTitleInputMode = reportTitleInputMode;
  }
  public setTemplateSubjectModelIds(subjectModelIds: string[]) {
    this.tempTemplate.subjectModelIds = subjectModelIds;
  }
  public isChangeTemplate(): boolean {
    if (this.originTemplate && this.tempTemplate) {
      return (
        this.templateForUndo.length > 0 ? true : false ||
        this.originTemplate.name !== this.tempTemplate.name ||
        this.originTemplate.isValid !== this.tempTemplate.isValid ||
        this.originTemplate.template.fontSize !== this.tempTemplate.template.fontSize ||
        this.originTemplate.template.fontFamily !== this.tempTemplate.template.fontFamily ||
        this.originTemplate.template.paperSize !== this.tempTemplate.template.paperSize ||
        this.originTemplate.template.pageMarginBottom !== this.tempTemplate.template.pageMarginBottom ||
        this.originTemplate.template.pageMarginTop !== this.tempTemplate.template.pageMarginTop ||
        this.originTemplate.template.pageMarginLeft !== this.tempTemplate.template.pageMarginLeft ||
        this.originTemplate.template.pageMarginRight !== this.tempTemplate.template.pageMarginRight ||
        this.originTemplate.template.header.height !== this.tempTemplate.template.header.height ||
        this.originTemplate.template.footer.height !== this.tempTemplate.template.footer.height ||
        this.originTemplate.reportTitleInputMode !==
          this.tempTemplate.reportTitleInputMode ||
        JSON.stringify(this.originTemplate.reportTitleFormat, undefined, 4) !==
          JSON.stringify(this.tempTemplate.reportTitleFormat, undefined, 4)
      );
    } else {
      return false;
    }
  }

  // metadataがレポート上に表示されているか検証
  public isDisplayObject(uuid: string): boolean {
    const pageIds = this.getPageIds();
    return pageIds.some((pageId) =>
      this.previewElementsIds(pageId).some((metaId) => metaId === uuid)
    );
  }

  public createErrorList(): ValidationErrorDetails[] {
    const errorList: ValidationErrorDetails[] = [];
    const template = this.getTemplate();
    if (template.header && template.header.texts) {
      template.header.texts.forEach((text: TemplateText) => {
        const reportText: ReportText = this.reportModel.getReportText(
          text.id,
          'header'
        );
        this.ValidationError(text, 'header', reportText, errorList);
      });
    }
       if (template.bodies) {
      template.bodies.forEach((body: TemplatePageSection) => {
        body.texts.forEach((text: TemplateText) => {
          const reportText: ReportText = this.reportModel.getReportText(
            text.id,
            'bodies'
          );
          this.ValidationError(text, 'bodies', reportText, errorList);
        });
        const summaryTable = body.inspectionSummary;
        if(summaryTable !== null){
          const reportSummary: ReportSummary = this.reportModel.getReportSummary(
            summaryTable.id,
            'bodies'
          );
          this.ValidationError(summaryTable.title, 'bodies', reportSummary, errorList);
          summaryTable.table.columns.forEach(column => {
            column.textsInfo.forEach(textsInfo => {
              this.ValidationError(textsInfo, 'bodies', reportSummary, errorList);
            })
          });
        }

        const subtotalTable = body.subTotal;
        if(subtotalTable !== null){
          const reportSubTotal: ReportSubTotal = this.reportModel.getReportSubtotal(
             subtotalTable.id,
            'bodies'
          );
          this.ValidationError(subtotalTable.title, 'bodies', reportSubTotal, errorList);
        }

        const pictureTable = body.inspectionPictures;
        if(pictureTable !== null){
          const reportPicture: ReportSummary = this.reportModel.getReportPicture(
            pictureTable.id,
           'bodies'
         );
         this.ValidationError(pictureTable.title, 'bodies', reportPicture, errorList);
        }

        const dataPlate = body.dataPlates;
        if(dataPlate !== null){
          const reportDataPlate: ReportDataPlate = this.reportModel.getReportDataPlate(
            dataPlate.id,
           'bodies'
         );
         this.ValidationError(dataPlate.title, 'bodies', reportDataPlate, errorList);
        }
      });
    }
    if (template.footer && template.footer.texts) {
      template.footer.texts.forEach((text: TemplateText) => {
        const reportText: ReportText = this.reportModel.getReportText(
          text.id,
          'footer'
        );
        this.ValidationError(text, 'footer', reportText, errorList);
      });
    }
    return errorList;
  }

  public ValidationError(
    text: TemplateText,
    location: string,
    reportInfo: any,
    errorList: ValidationErrorDetails[]
  ){
    if (text.inputType === 'select' || text.inputType === 'multiSelect') {
      if (this.hasEmptyOptError(text, location, reportInfo)) {
        errorList.push(this.hasEmptyOptError(text, location, reportInfo));
      }
      if (this.hasMaxLengthError(text, location, reportInfo)) {
        errorList.push(this.hasMaxLengthError(text, location, reportInfo));
      }
    }
    if (text.type === 'dynamic-editable') {
      if (this.hasEmptyEditableIdError(text, location, reportInfo) !== null) {
        errorList.push(this.hasEmptyEditableIdError(text, location, reportInfo));
      }
      if (this.hasCapitalLetterError(text, location, reportInfo) !== null) {
        errorList.push(this.hasCapitalLetterError(text, location, reportInfo));
      }
      if (this.hasEmptyValidationError(text, location, reportInfo) != null) {
        errorList.push(this.hasEmptyValidationError(text, location, reportInfo));
      }
    }

    return errorList;
  }

  // 最大長検証で引っかかったOptionをエラーリストに格納
  public hasMaxLengthError(
    text: TemplateText,
    location: string,
    reportInfo: any
  ): ValidationErrorDetails {
    if (text.options && text.options.length > 0) {
      // const reportText: ReportText = this.reportModel.getReportText(
      //   text.id,
      //   location
      // );
      if (
        text.validation && text.validation.maxLength && text.options.some((opt) => opt.value.length > text.validation.maxLength)
      ) {
        return new ValidationErrorDetails(
          reportInfo,
          'options',
          ERROR_MESSAGE.LENGTH,
          this.translate,
          this.langService,
        );
      }
      return null;
    }
  }

  // 必須検証で引っかかったOptionをエラーリストに格納
  public hasEmptyOptError(
    text: TemplateText,
    location: string,
    reportInfo: any
  ): ValidationErrorDetails {
    if (
      !text.options ||
      text.options.length === 0 ||
      text.options.some((opt) => opt.label.length < 1 || opt.value.length < 1)
    ) {
      // const reportText: ReportText = this.reportModel.getReportText(
      //   text.id,
      //   location
      // );
      // console.log('reportText', reportText);
      return new ValidationErrorDetails(
        reportInfo,
        'options',
        ERROR_MESSAGE.REQUIRED,
        this.translate,
        this.langService,
      );
    }
    return null;
  }

  // 必須検証で引っかかったValidationをエラーリストに格納
  public hasEmptyValidationError(
    text: TemplateText,
    location: string,
    reportInfo: any
  ): ValidationErrorDetails {
    if (
      !text.validation
    ) {
      // const reportText: ReportText = this.reportModel.getReportText(
      //   text.id,
      //   location
      // );
      // console.log('reportText', reportText);
      return new ValidationErrorDetails(
        reportInfo,
        'maxLength, required',
        ERROR_MESSAGE.REQUIRED,
        this.translate,
        this.langService
      );
    }
    return null;
  }

  // 必須検証で引っかかったeditableIdをエラーリストに格納
  public hasEmptyEditableIdError(
    text: TemplateText,
    location: string,
    reportInfo: any
  ): ValidationErrorDetails {
    if (!text.editableId) {
      // const reportText: ReportText = this.reportModel.getReportText(
      //   text.id,
      //   location
      // );
      return new ValidationErrorDetails(
        reportInfo,
        'editableId',
        ERROR_MESSAGE.REQUIRED,
        this.translate,
        this.langService,
      );
    }
    return null;
  }

  // 先頭大文字検証で引っかかったeditableIdをエラーリストに格納
  public hasCapitalLetterError(
    text: TemplateText,
    location: string,
    reportInfo: any
  ): ValidationErrorDetails {
    if (
      this.isVisol &&
      text.editableId &&
      !this.isLowerCamelCase(text.editableId)
    ) {
      // const reportText: ReportText = this.reportModel.getReportText(
      //   text.id,
      //   location
      // );
      return new ValidationErrorDetails(
        reportInfo,
        'editableId',
        ERROR_MESSAGE.LOWERCASE,
        this.translate,
        this.langService,
      );
    }
    return null;
  }

  // editableIdの入力文字チェック
  public isLowerCamelCase(textField): boolean {
    return REGULAR_EXPRESSION.SMALL_LETTERS_PATTERN.test(textField[0]);
  }

  // FieldType:Text
  public validatorText(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:Number
  public validatorNumber(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(
      Validators.pattern(REGULAR_EXPRESSION.DECIMALPATTERN)
    );
    return validator;
  }

  // FieldType:Color
  public validatorColor(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:Integer
  public validatorInteger(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(
      Validators.pattern(REGULAR_EXPRESSION.NUMBERPATTERN)
    );
    return validator;
  }

  // FieldType:TextArea
  public validatorTextArea(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:JSON
  public validatorJSON(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Multi
  public validatorMulti(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Select
  public validatorSelect(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    return validator;
  }

  // FieldType:CheckBox
  public validatorCheckBox(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return null;
  }

  public loadReportTemplateImages() {
    this.reportTemplateService
      .getReportTemplateImages(this.templateId, this.isVisol)
      .subscribe((images: ReportGlobalImage[]) => {
        this.templateImages = images;
        this.onReady$.next('ready');
      });
  }
  public ready() {
    this.onReady$.next('ready');
  }

  public getTargetIdList(): Observable<ResponseIdList[]> {
    return this.reportTemplateService.getModelsForTemplates(
      this.templateId,
      null,
    );
  }

  public updateModelsForTemplates(
    updateIdList: UpdateIdList
  ): Observable<UpdateResultList> {
    return this.reportTemplateService.updateModelsForTemplates(
      this.templateId,
      updateIdList,
      this.isVisol
    );
  }

  public getReportTemplateImages(): { label: string; value?: string }[] {
    const tempImages: { label: string; value?: string, noTranslate: boolean, }[] = [];
    this.templateImages.forEach((image: ReportGlobalImage) => {
      tempImages.push({
        label: image.name,
        value: this.isVisol ? image.name : image.imageUrl,
        noTranslate: true,
      });
    });
    return tempImages;
  }

  public getReportDynamicDataFields(elementType?: string): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    if (
      this.dynamicDataFields &&
      this.dynamicDataFields.Text &&
      this.dynamicDataFields.Text.length > 0
    ) {
      this.dynamicDataFields.Text.forEach((dataField: string) => {
        tempDataFields.push({ label: dataField, value: dataField });
      });
    } else if (this.isVisol && this.dynamicDataFields) {
      switch (elementType){
        case ElementType.Text:
          this.dynamicDataFields = DYNAMIC_SELECT_ITEM_TEXT_VISOL;
          break;
        case ElementType.DataPlate:
          this.dynamicDataFields = DYNAMIC_SELECT_ITEM_DATAPLATE_VISOL;
          break;
        case ElementType.Summary:
          this.dynamicDataFields = DYNAMIC_SELECT_ITEM_SUMMARYTABLE_VISOL;
          break;
        case ElementType.SubTotal:
          this.dynamicDataFields = DYNAMIC_SELECT_ITEM_SUBTOTALTABLE_VISOL;
          break;
        case ElementType.Picture:
          this.dynamicDataFields = DYNAMIC_SELECT_ITEM_PICTURETABLE_VISOL;
          break;
        default:
          break;
      }

      this.dynamicDataFields.forEach(
        (dataField: { label: string; value?: string }) => {
          tempDataFields.push(dataField);
        }
      );
    }
    return tempDataFields;
  }

  public getMeasurementPointDataFields(): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    if (
      this.dynamicDataFields &&
      this.dynamicDataFields.MeasurementPointDataField
    ) {
      this.dynamicDataFields.MeasurementPointDataField.forEach(
        (dataField: string) => {
          tempDataFields.push({ label: dataField, value: dataField });
        }
      );
    }
    return tempDataFields;
  }

  public getVisualEventDataFields(): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    this.dynamicDataFields.MeasurementVisualEventDataField.forEach(
      (dataField: string) => {
        tempDataFields.push({ label: dataField, value: dataField });
      }
    );
    return tempDataFields;
  }

  public getImageUrl(fileName: string): string {
    let imageURL: string;
    this.templateImages.forEach((image: ReportGlobalImage) => {
      if (fileName === image.name) {
        imageURL = image.imageUrl;
      }
    });
    return imageURL;
  }

  public getElementType(): ElementType {
    return this.drawElementType;
  }
  public notifySelectElement(
    notifier: Notifier,
    targetId: string,
    isCtrl: boolean
  ) {
    this.reportModel.selectElement(targetId, isCtrl);
    this.onSelectedElement$.next('');
  }
  public notifySelectPage(error) {
    const pageNumber = 'p' + (error.pageIndex + 1);
    this.router.navigate([], { fragment: pageNumber });
    document.querySelector('#' + pageNumber).scrollIntoView();
  }
  public notifyChangeScroll(notifier: Notifier, targetId: string, isPageSelected?: boolean) {
    if (targetId !== this.viewPageId) {
      this.reportModel.changePage(targetId);
      this.onSelectedElement$.next('');
      this.viewPageId = targetId;
    }
    else if (isPageSelected) {
      this.reportModel.changePage(targetId);
      this.onSelectedElement$.next('');
    }
  }

  public notifyDeselectAll(notifier: Notifier, isCtrl: boolean) {
    this.reportModel.deselectAll(isCtrl);
    this.onSelectedElement$.next('');
  }

  public notifyAddPage(notifier: Notifier): void {
    const template = { ...this.originTemplate };
    template.reportTemplateContent = JSON.stringify(this.getTemplate());
    this.templateForUndo.push(template);
    this.reportModel.addPage(this.viewPageId);
    this.onSelectedElement$.next('');
    //Below code is added for after page added template refresh for element settings
    const templateAfterAdded: ReportTemplate = { ...this.originTemplate };
    templateAfterAdded.reportTemplateContent = JSON.stringify(
      this.getTemplate()
    );
    templateAfterAdded.template = this.getTemplate();
    this.loadReportTemplate(templateAfterAdded, true);
    this.highlightElement = false;
  }
  public notifyAddCustomPage(): void {
    this.reportModel.addCustomPage();
    this.onSelectedElement$.next('');
  }

  public deletePage(pageId: string) {
    const template: ReportTemplate = { ...this.originTemplate };
    template.template = this.getTemplate();
    template.template.bodies = template.template.bodies.filter(x => x.id !== this.getPage(pageId).getMetadataId());
    this.loadReportTemplate(template,true);
  }

  public saveCustomTemplate(name: string, application: string): Observable<any> {
    const addCustomElement = new AddCustomElement();
    addCustomElement.name = name;
    addCustomElement.application = application;
    addCustomElement.templateUrl = JSON.stringify(this.customTemplateDefinition);

    return new Observable<any>((subscriber) => {
      lastValueFrom(this.reportComponentService.createCustomTemplate(addCustomElement.templateUrl, name,application )).then()
      .catch(()=>{
        subscriber.error();
      }).finally(()=>{
        subscriber.complete();
      });
    });
  }

  public notifyDrawMode(notifier: Notifier, drawType: ElementType, customElementId?: string, customElementName?: string): void {
    this.drawElementType = drawType;
    this.drawDesignCustomElementId = customElementId;
    this.drawDesignCustomElementName = customElementName;
    this.reportModel.beginDrawMode();
  }

  public notifyDraw(
    notifier: Notifier,
    location: Location,
    metadata: ReportPreviewMetadata
  ): void {
    const pageId = location === Location.Body ? notifier.ids.pageId : undefined;
    metadata = this.fitVirtualGrid(metadata); // 仮想Gridにfitする座標値になるよう再計算する
    this.reportModel.drawElement(
      location,
      this.drawElementType,
      metadata,
      pageId
    );
    this.reportModel.endDrawMode();
    this.onEndDraw$.next('');
    if (this.drawElementType?.toLowerCase() === 'customelement') {
      const updateDesign: UpdateMetadata = {
        id:this.getSelectedElementProperties()[1].getId(),
        name:'designElementId',
        oldValue:'',
        parentName:null,
        value:this.drawDesignCustomElementId
      };
      this.notifyUpdateElementProperties(notifier, updateDesign);
      const updateName: UpdateMetadata = {
        id:this.getSelectedElementProperties()[1].getId(),
        name:'designElementName',
        oldValue:'',
        parentName:null,
        value:this.drawDesignCustomElementName
      };
      this.notifyUpdateElementProperties(notifier, updateName);
    }
    this.onSelectedElement$.next('');
    delete this.drawElementType;
    delete this.drawDesignCustomElementId;
    delete this.drawDesignCustomElementName;
  }

  public duplicateElements(
    location: Location,
    elementType: ElementType,
    baseDef: any,
    pageId: string
  ): void {
    this.reportModel.copySelected(location, elementType, [baseDef], pageId);
  }

  public notifyDrawCancel(notifier: Notifier) {
    this.reportModel.endDrawMode();
    this.onEndDraw$.next('');
    delete this.drawElementType;
    delete this.drawDesignCustomElementId;
    delete this.drawDesignCustomElementName;
  }

  public notifyBeginDrag(
    notifier: Notifier,
    dragElementType: ElementType,
    fromDetail: FromToDetail,
    dragMousePoint: MousePoint
  ) {
    if (!this.dragElements) {
      this.dragElements = {};
    }
    this.dragElements[notifier.ids.elementId] = {
      type: dragElementType,
      from: fromDetail,
      dragPoint: dragMousePoint,
    };
  }

  public notifyDrop(
    notifier: Notifier,
    dragElemId: string,
    toDetail: FromToDetail,
    dropMousePoint: MousePoint
  ) {
    const dragElement = this.dragElements[dragElemId];

    // 仮想Gridにfitする座標値でmetadata生成
    const dropMetadata: ReportPreviewMetadata = this.fitVirtualGrid({
      topPx: dropMousePoint.onElementY - dragElement.dragPoint.onElementY,
      leftPx: dropMousePoint.onElementX - dragElement.dragPoint.onElementX,
      widthPx: 0, // ドロップ操作でwidthは変更できないのでダミー値を設定
      heightPx: 0, // ドロップ操作でheightは変更できないのでダミー値を設定
    });

    const fromTo: FromTo = {
      from: dragElement.from,
      to: toDetail,
    };

    this.reportModel.dropElement(
      dragElemId,
      dragElement.type,
      dropMetadata,
      fromTo
    );
    delete this.dragElements[dragElemId];
  }

  public notifyBeginResize(
    notifier: Notifier,
    type: ElementType,
    grabPointH: string,
    grabPointV: string,
    beginPoint: MousePoint
  ) {
    if (!this.resizeElements) {
      this.resizeElements = {};
    }
    this.resizeElements[notifier.ids.elementId] = {
      type,
      markers: {
        horizontal: grabPointH,
        vertical: grabPointV,
      },
      beginPoint,
    };
  }

  public notifyResize(notifier: Notifier, resizePoint: MousePoint) {
    const resizeBegin = this.resizeElements[notifier.ids.elementId].beginPoint;
    const markers = this.resizeElements[notifier.ids.elementId].markers;

    const resizeOffset: ReportPreviewMetadata = this.fitVirtualGrid({
      topPx: resizePoint.onElementY - resizeBegin.onElementY,
      leftPx: resizePoint.onElementX - resizeBegin.onElementX,
      widthPx: resizePoint.onElementX - resizeBegin.onElementX,
      heightPx: resizePoint.onElementY - resizeBegin.onElementY,
    });

    this.reportModel.resizeElement(
      notifier.ids.elementId,
      resizeOffset,
      markers.horizontal,
      markers.vertical
    );
    this.onUpdatePageSnap$.next('');
  }

  public notifyDelete(notifier: Notifier): void {
    this.notifyDrawCancel(notifier);
    this.reportModel.deleteSelected();
    this.onUpdatePageSnap$.next('');
  }

  public deleteElement(id: string, location: Location): void {
    this.reportModel.deleteElementsById(id, location);
  }

  public notifyUpdateCommonProperties(
    notifier: Notifier,
    updateMetadata: UpdateMetadata
  ) {
    this.reportModel.updateCommonProperties(updateMetadata);
  }

  public notifyUpdateElementProperties(
    notifier: Notifier,
    updateMetadata: UpdateMetadata
  ) {
    this.reportModel.updateElementProperties(updateMetadata);
    if (
      this.reportModel.getElement(updateMetadata.id).getElementType() ===
      'Section'
    ) {
      this.reportModel.updateStageTagForSection(updateMetadata.id);
    }
  }

  public fitVirtualGrid(src: ReportPreviewMetadata): ReportPreviewMetadata {
    const calcGridPx = (value: number): number => {
      const useMinus: boolean = value < 0;
      const mod = Math.abs(value) % this.virtualGridPx;
      let rtnValue = Math.abs(value);

      rtnValue =
        mod < this.virtualGridPx / 2
          ? rtnValue - mod
          : rtnValue + (this.virtualGridPx - mod);
      rtnValue = useMinus && rtnValue !== 0 ? rtnValue * -1 : rtnValue;

      // console.log('calc log: %s -> %s (mod: %s, under: %s, over: %s)',
      //   value,
      //   rtnValue,
      //   mod,
      //   Math.abs(value) - mod,
      //   Math.abs(value) + (this.virtualGridPx - mod)
      // );

      return rtnValue;
    };
    src.heightPx = calcGridPx(src.heightPx);
    src.widthPx = calcGridPx(src.widthPx);
    src.topPx = calcGridPx(src.topPx);
    src.leftPx = calcGridPx(src.leftPx);

    return src;
  }

  public getCommonProperties(): ReportProperties[] {
    return this.reportModel.getCommonProperties();
  }

  public getSelectedElementProperties(): ReportProperties[] {
    return this.reportModel.getSelectedElementProperties();
  }

  public previewElementsIds(pageId: string): string[] {
    return this.reportModel.previewElementsIds(pageId);
  }
  public getPageIds(): string[] {
    return this.reportModel.getPageIds();
  }
  public getTextIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Text');
  }
  public getLineIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Line');
  }
  public getImageIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Image');
  }
  public getRectangleIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Rectangle');
  }
  public getSummaryIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Summary');
  }
  public getSubTotalIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'SubTotal');
  }
  public getPictureIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Picture');
  }
  public getDataPlateIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'DataPlate');
  }
  public getMeasurementPointIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'MeasurementPoint');
  }
  public getVisualEventsIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'VisualEvents');
  }
  public getSignatureIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Signature');
  }
  public getTableIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Table');
  }
  public getInteractiveIsoIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'InteractiveIso');
  }
  public getDropDownListIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'DropDownList');
  }

  public getSectionIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Section');
  }
  public getCustomElementIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'CustomElement');
  }

  public getPage(pageId: string): ReportPage {
    return this.reportModel.getPage(pageId);
  }
  public getText(textId: string): ReportText {
    return this.reportModel.getText(textId);
  }
  public getLine(lineId: string): ReportLine {
    return this.reportModel.getLine(lineId);
  }
  public getImage(imageId: string): ReportImage {
    return this.reportModel.getImage(imageId);
  }
  public getMeasurementPoints(tableId: string): ReportMeasurementPoint {
    return this.reportModel.getMeasurementPoint(tableId);
  }
  public getRectangle(rectangleId: string): ReportRectangle {
    return this.reportModel.getRectangle(rectangleId);
  }
  public getSummary(summaryId: string): ReportSummary {
    return this.reportModel.getSummary(summaryId);
  }
  public getSubTotal(subtotalId: string): ReportSubTotal {
    return this.reportModel.getSubTotal(subtotalId);
  }
  /* public getPicture(pictureId: string): ReportPicture {
    return this.reportModel.getPicture(pictureId);
  } */
  public getDataPlate(dataPlateId: string): ReportDataPlate {
    return this.reportModel.getDataPlate(dataPlateId);
  }
  public getVisualEvents(visualEventId: string): ReportVisualEvents {
    return this.reportModel.getVisualEvent(visualEventId);
  }
  public getSignature(signatureId: string): ReportSignature {
    return this.reportModel.getSignature(signatureId);
  }
  public getSection(sectionId: string): ReportSection {
    return this.reportModel.getSection(sectionId);
  }
  public getTable(tableId: string): ReportTable {
    return this.reportModel.getTable(tableId);
  }
  public getInterActiveIso(isoId: string): ReportISO {
    return this.reportModel.getInterActiveIso(isoId);
  }
  public getDropDownList(ddlId: string): ReportDropDownList {
    return this.reportModel.getDropDownList(ddlId);
  }
  public getCustomElements(ceId: string): ReportCustomElement {
    return this.reportModel.getCustomElement(ceId);
  }

  public getPaperHeightPx(): number {
    return this.reportModel.getPaperHeightPx();
  }
  public saveReportTemplate(): Observable<any> {
    const reportTemplateUpdate = new ReportTemplateUpdate();
    reportTemplateUpdate.id = this.tempTemplate.id;
    reportTemplateUpdate.name = this.tempTemplate.name;
    reportTemplateUpdate.application = this.tempTemplate.application;
    reportTemplateUpdate.createdByTenantId = this.tempTemplate.id;
    reportTemplateUpdate.createdByUserId = this.tempTemplate.id;
    reportTemplateUpdate.createdDateUtc = new Date();
    reportTemplateUpdate.lastUpdatedDateUtc = new Date();

    reportTemplateUpdate.reportTemplateContent = JSON.stringify(this.textTypeShaping());
    reportTemplateUpdate.subjectModelIds = this.tempTemplate.subjectModelIds;
    reportTemplateUpdate.reportType = this.tempTemplate.reportType
      ? this.tempTemplate.reportType
      : 'Unknown';
    reportTemplateUpdate.equipmentType = this.tempTemplate.equipmentType
      ? this.tempTemplate.equipmentType
      : '';
    reportTemplateUpdate.etag = this.tempTemplate.etag;
    reportTemplateUpdate.isValid = this.tempTemplate.isValid;
    reportTemplateUpdate.reportTitleFormat =
      this.tempTemplate.reportTitleFormat;
    reportTemplateUpdate.reportTitleInputMode =
      this.tempTemplate.reportTitleInputMode;
    reportTemplateUpdate.schemaVersion = this.tempTemplate.schemaVersion;
    return new Observable<any>((subscriber) => {
      lastValueFrom(this.reportTemplateService.updateReportTemplate(reportTemplateUpdate, this.isVisol))
        .then(() => {
          lastValueFrom(this.reportTemplateService.getReportTemplate(reportTemplateUpdate.id, this.isVisol))
          .then((reportTemplate) => {
            this.originTemplate = reportTemplate;
            this.originTemplate.template = reportTemplate.reportTemplateContent
              ? JSON.parse(reportTemplate.reportTemplateContent)
              : new TemplateDefinition();
            this.tempTemplate = JSON.parse(JSON.stringify(this.originTemplate));
            this.tempTemplate.template = JSON.parse(JSON.stringify(this.originTemplate.template));
            this.templateForUndo = [];
            this.templateForRedo = [];
          });
        })
        .finally(() => {
          subscriber.complete();
        });
    });
  }

  // テキストリストをバックエンドに送れる型に成型
  public textTypeShaping(): any {
    const tmpTemplate = JSON.parse(JSON.stringify(this.getTemplate())) as any;

    tmpTemplate.bodies?.forEach(page => {
      page.inspectionSummary?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => typeof (text) === 'string' ? text : text?.text);
      });
      page.inspectionSummary?.subTotal?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => typeof (text) === 'string' ? text : text?.text);
      });
    });

    tmpTemplate.bodies?.forEach(page => {
      page.inspectionPictures?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => typeof (text) === 'string' ? text : text?.text);
      });
    });

    return tmpTemplate;
  }

  public getTemplate(): TemplateDefinition {
    const model = this.reportModel;
    const tempTemplate: TemplateDefinition = JSON.parse(
      JSON.stringify(this.tempTemplate.template, undefined, 4)
    );
    const tempHeader: TemplateCommonSection = tempTemplate.header;
    // TODO 一時的にid付与のためのコード。別途削除
    // tempHeader.id = this.generateUUID();
    const headerTexts: {
      [elementId: string]: ReportText;
    } = model.getHeaderTexts();
    tempHeader.texts = [];
    for (const id of Object.keys(headerTexts)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // headerTexts[id].getTemplateJSON().id = this.generateUUID();
      tempHeader.texts.push(headerTexts[id].getTemplateJSON());
    }
    const headerImages: {
      [elementId: string]: ReportImage;
    } = model.getHeaderImages();
    tempHeader.images = [];
    for (const id of Object.keys(headerImages)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // headerImages[id].getTemplateJSON().id = this.generateUUID();
      tempHeader.images.push(headerImages[id].getTemplateJSON());
    }
    const headerLines: {
      [elementId: string]: ReportLine;
    } = model.getHeaderLines();
    tempHeader.lines = [];
    for (const id of Object.keys(headerLines)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // headerLines[id].getTemplateJSON().id = this.generateUUID();
      tempHeader.lines.push(headerLines[id].getTemplateJSON());
    }
    const headerRectangles: { [elementId: string]: ReportRectangle } =
      model.getHeaderRectangles();
    for (const id of Object.keys(headerRectangles)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // headerRectangles[id].getTemplateJSON().id = this.generateUUID();
      tempHeader.rectangles.push(headerRectangles[id].getTemplateJSON());
    }
    if (!this.isVisol) {
      const headerSignatures: { [elementId: string]: ReportSignature } =
        model.getHeaderSignatures();
      tempHeader.signatures = [];
      for (const id of Object.keys(headerSignatures)) {
        tempHeader.signatures.push(headerSignatures[id].getTemplateJSON());
      }
      const headerTables: { [elementId: string]: ReportTable } =
        model.getHeaderTables();
      tempHeader.tables = [];
      for (const id of Object.keys(headerTables)) {
        tempHeader.tables.push(headerTables[id].getTemplateJSON());
      }
    }
    const tempBodies: TemplatePageSection[] = tempTemplate.bodies;
    tempBodies.length = 0;
    for (const id of model.getPageIds()) {
      const tempPage: TemplatePageSection = model.getPage(id).getTemplateJSON();
      // TODO 一時的にid付与のためのコード。別途削除
      // const tempPage: any = model.getPage(id).getTemplateJSON();
      // if (tempPage.inspectionPictures) {
      //  tempPage.inspectionPictures.id = this.generateUUID();
      //  tempPage.inspectionPictures.title.id = this.generateUUID();
      //  tempPage.inspectionPictures.table.id = this.generateUUID();
      // }
      // if (tempPage.inspectionSummary) {
      //  tempPage.inspectionSummary.id = this.generateUUID();
      //  tempPage.inspectionSummary.title.id = this.generateUUID();
      //  tempPage.inspectionSummary.table.id = this.generateUUID();
      // }

      const bodyTextKeys: string[] = this.getTextIds(id, 'Body');
      tempPage.texts = [];
      for (const textId of bodyTextKeys) {
        // TODO 一時的にid付与のためのコード。別途削除
        // this.getText(textId).getTemplateJSON().id = this.generateUUID();
        tempPage.texts.push(this.getText(textId).getTemplateJSON());
      }
      const bodyLineKeys = this.getLineIds(id, 'Body');
      tempPage.lines = [];
      for (const lineId of bodyLineKeys) {
        // TODO 一時的にid付与のためのコード。別途削除
        // this.getLine(lineId).getTemplateJSON().id = this.generateUUID();
        tempPage.lines.push(this.getLine(lineId).getTemplateJSON());
      }
      const bodyImageKeys = this.getImageIds(id, 'Body');
      tempPage.images = [];
      for (const imageId of bodyImageKeys) {
        // TODO 一時的にid付与のためのコード。別途削除
        // this.getImage(imageId).getTemplateJSON().id = this.generateUUID();
        tempPage.images.push(this.getImage(imageId).getTemplateJSON());
      }
      const bodyRectangleKeys = this.getRectangleIds(id, 'Body');
      tempPage.rectangles = [];
      for (const rectangleId of bodyRectangleKeys) {
        // TODO 一時的にid付与のためのコード。別途削除
        // this.getRectangle(rectangleId).getTemplateJSON().id = this.generateUUID();
        tempPage.rectangles.push(
          this.getRectangle(rectangleId).getTemplateJSON()
        );
      }
      if (!this.isVisol) {
        const bodyMeasurementPointKeys = this.getMeasurementPointIds(
          id,
          'Body'
        );
        tempPage.measurementPoints = [];
        for (const measurementPointKey of bodyMeasurementPointKeys) {
          tempPage.measurementPoints.push(
            this.getMeasurementPoints(measurementPointKey).getTemplateJSON()
          );
        }
        const bodyVisualEventsKeys = this.getVisualEventsIds(id, 'Body');
        tempPage.visualEvents = [];
        for (const visualEventId of bodyVisualEventsKeys) {
          tempPage.visualEvents.push(
            this.getVisualEvents(visualEventId).getTemplateJSON()
          );
        }
        const bodySignatureKeys = this.getSignatureIds(id, 'Body');
        tempPage.signatures = [];
        for (const signatureId of bodySignatureKeys) {
          tempPage.signatures.push(
            this.getSignature(signatureId).getTemplateJSON()
          );
        }
        const bodySectionKeys = this.getSectionIds(id, 'Body');
        tempPage.sections = [];
        for (const sectionId of bodySectionKeys) {
          tempPage.sections.push(this.getSection(sectionId).getTemplateJSON());
        }
        const bodyTableKeys = this.getTableIds(id, 'Body');
        tempPage.tables = [];
        for (const tableId of bodyTableKeys) {
          tempPage.tables.push(this.getTable(tableId).getTemplateJSON());
        }
        const bodyInteractiveIsoKeys = this.getInteractiveIsoIds(id, 'Body');
        tempPage.interactiveIso = [];
        for (const isoId of bodyInteractiveIsoKeys) {
          tempPage.interactiveIso.push(
            this.getInterActiveIso(isoId).getTemplateJSON()
          );
        }
        const bodyDropDownListKeys = this.getDropDownListIds(id, 'Body');
        tempPage.dropDownLists = [];
        for (const ddlId of bodyDropDownListKeys) {
          tempPage.dropDownLists.push(
            this.getDropDownList(ddlId).getTemplateJSON()
          );
        }
        const bodyCustomElementKeys = this.getCustomElementIds(id, 'Body');
        tempPage.customElements = [];
        for (const ceId of bodyCustomElementKeys) {
          tempPage.customElements.push(
            this.getCustomElements(ceId).getTemplateJSON()
          );
        }
      } else {
        const bodySummaryKeys = this.getSummaryIds(id, 'Body');
        const summary = this.getSummary(bodySummaryKeys[0]);
        if (summary) {
          tempPage.inspectionSummary = summary.getTemplateJSON();
          /* console.log({
            json: summary.getTemplateJSON(),
            json2: summary
          }); */
        } else {
          tempPage.inspectionSummary = null;
        }
        const bodySubTotalKeys = this.getSubTotalIds(id, 'Body');
        const subtotal = this.getSummary(bodySubTotalKeys[0]);
        if (subtotal) {
          tempPage.subTotal = subtotal.getTemplateJSON();
          // console.log({
          //   json: subtotal.getTemplateJSON(),
          //   json2: subtotal
          //}); 
        } else {
          tempPage.subTotal = null;
        }
        const bodyPictureKeys = this.getPictureIds(id, 'Body');
        const picture = this.getSummary(bodyPictureKeys[0]);
        if (picture) {
          tempPage.inspectionPictures = picture.getTemplateJSON();
          /* console.log({
              json: picture.getTemplateJSON(),
              json2: picture

            }); */
        } else {
          tempPage.inspectionPictures = null;
        }
        const bodyDataPlateKeys = this.getDataPlateIds(id, 'Body');
        const dataPlate = this.getDataPlate(bodyDataPlateKeys[0]);
        if (dataPlate) {
          tempPage.dataPlates = dataPlate.getTemplateJSON();
        }
        else {
          tempPage.dataPlates = null;
        }
      }
      tempBodies.push(tempPage);
    }

    const tempFooter: TemplateCommonSection = tempTemplate.footer;
    // TODO 一時的にid付与のためのコード。別途削除
    // tempFooter.id = this.generateUUID();
    const footerTexts: {
      [elementId: string]: ReportText;
    } = model.getFooterTexts();
    tempFooter.texts = [];
    for (const id of Object.keys(footerTexts)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // footerTexts[id].getTemplateJSON().id = this.generateUUID();
      tempFooter.texts.push(footerTexts[id].getTemplateJSON());
    }
    const footerImages: {
      [elementId: string]: ReportImage;
    } = model.getFooterImages();
    tempFooter.images = [];
    for (const id of Object.keys(footerImages)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // footerImages[id].getTemplateJSON().id = this.generateUUID();
      tempFooter.images.push(footerImages[id].getTemplateJSON());
    }
    const footerLines: { [elementId: string]: ReportLine } =
      model.getFooterLines();

    tempFooter.lines = [];
    for (const id of Object.keys(footerLines)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // footerLines[id].getTemplateJSON().id = this.generateUUID();
      tempFooter.lines.push(footerLines[id].getTemplateJSON());
    }
    const footerRectangles: { [elementId: string]: ReportRectangle } =
      model.getFooterRectangles();

    tempFooter.rectangles = [];
    for (const id of Object.keys(footerRectangles)) {
      // TODO 一時的にid付与のためのコード。別途削除
      // footerRectangles[id].getTemplateJSON().id = this.generateUUID();
      tempFooter.rectangles.push(footerRectangles[id].getTemplateJSON());
    }
    if (!this.isVisol) {
      const footerSignatures: { [elementId: string]: ReportSignature } =
        model.getFooterSignatures();
      tempFooter.signatures = [];
      for (const id of Object.keys(footerSignatures)) {
        tempFooter.signatures.push(footerSignatures[id].getTemplateJSON());
      }
      const footerTables: { [elementId: string]: ReportTable } =
        model.getFooterTables();
      tempFooter.tables = [];
      for (const id of Object.keys(footerTables)) {
        tempFooter.tables.push(footerTables[id].getTemplateJSON());
      }
    }

    return tempTemplate;
  }

  public alignElementsWithSectionOnPage(pageId: string): void {
    const sectionIds = this.getSectionIds(pageId, 'Body');
    if (sectionIds.length === 0) {
      return;
    }
    this.reportModel.alignElementsWithSection(pageId);
  }

  public updateStageTagForSection(sectionId: string): void {
    this.reportModel.updateStageTagForSection(sectionId);
  }

  public getStageName() {
    return this.stageNames;
  }
  public getViewPageId() {
    return this.viewPageId;
  }
  // TODO 一時的にid付与のためのコード。別途削除
  /**
   * UUIDを生成する
   * 流用ロジック：https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
   * ライセンス  ：Apache License v2.0
   */
  private generateUUID(): string {
    const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('');
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join('');
  }
}
